import React, { useState } from 'react';
import { Box, Container, Typography, Card, CardContent, Button, Slider, Grid, Switch, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { PRICING_PLANS, appSignUp } from '../constants/constants';

export default function PricingCards() {
    const { t } = useTranslation();
    const [sliderValue, setSliderValue] = useState(10);
    const [isAnnual, setIsAnnual] = useState(true);

    const handleSliderChange = (event, newValue) => {
        setSliderValue(newValue);
    };

    const getPrice = (planType, isClub = false) => {
        if (isClub) {
            // 1 admin + (número de entrenadores × precio entrenador vinculado)
            const adminPrice = isAnnual ? PRICING_PLANS.adminClub.monthlyFromYearly : PRICING_PLANS.adminClub.monthly;
            const coachPrice = isAnnual ? PRICING_PLANS.coachLinkedToAdmin.monthlyFromYearly : PRICING_PLANS.coachLinkedToAdmin.monthly;

            const totalPrice = parseFloat(adminPrice) + (parseFloat(coachPrice) * sliderValue);
            return totalPrice.toFixed(2);
        }

        if (planType === 'individual') {
            return isAnnual ? PRICING_PLANS.individual.monthlyFromYearly : PRICING_PLANS.individual.monthly;
        }

        return '0';
    };

    const pricingPlans = [
        {
            title: t('PricingCardsFree00'),
            price: `0€`,
            period: 'forever',
            features: [
                t('PricingCardsFree02'),
                t('PricingCardsFree03'),
                t('PricingCardsFree04'),
                t('PricingCardsFree05'),
                t('PricingCardsFree06'),
                t('PricingCardsFree07'),
                t('PricingCardsFree08'),
            ],
            popular: false,
            color: '#6b7280',
            isFree: true
        },
        {
            title: t('PricingCardsPro00'),
            price: `${getPrice('individual')}€`,
            period: isAnnual ? 'billed yearly' : 'month',
            features: [
                t('PricingCardsPro02'),
                t('PricingCardsPro03'),
                t('PricingCardsPro04'),
                t('PricingCardsPro05'),
                t('PricingCardsPro06'),
                t('PricingCardsPro07'),
                t('PricingCardsPro08'),
                t('PricingCardsPro09'),
            ],
            popular: true,
            color: '#048cec'
        },
        {
            title: t('PricingCardsClub00'),
            price: `${getPrice('adminClub', true)}€`,
            period: isAnnual ? 'billed yearly' : 'month',
            features: [
                t('PricingCardsClub02'),
                t('PricingCardsClub03'),
                t('PricingCardsClub04'),
            ],
            popular: false,
            color: '#fca444',
            hasSlider: true
        }
    ];

    return (
        <Box
            component="section"
            sx={{
                py: { xs: 8, md: 12 },
                px: { xs: 2, md: 4 },
                backgroundColor: 'background.default',
            }}
        >
            <Container maxWidth="lg">
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 2,
                        mb: 6,
                    }}
                >

                    <Typography
                        variant="h2"
                        sx={{
                            fontFamily: 'Barlow, sans-serif',
                            fontSize: { xs: '2rem', sm: '2.5rem', md: '2.8rem' },
                            fontWeight: 800,
                            lineHeight: 1.2,
                            color: '#1a202c',
                        }}
                    >
                        {t('PricingCards00')}
                    </Typography>

                    <Typography
                        sx={{
                            color: 'text.secondary',
                            maxWidth: 500,
                            fontFamily: 'Barlow, sans-serif',
                            fontSize: '1rem',
                            lineHeight: 1.5,
                        }}
                    >
                        {t('PricingCards01')}
                    </Typography>

                    {/* Annual/Monthly Toggle */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mt: 2,
                            typography: 'overline',
                            fontFamily: 'Barlow, sans-serif',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            letterSpacing: '0.1em',
                        }}
                    >
                        <Typography
                            sx={{
                                color: !isAnnual ? '#1a202c' : 'text.disabled',
                                fontFamily: 'Barlow, sans-serif',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                letterSpacing: '0.1em',
                            }}
                        >
                            {t('PricingCards03')}
                        </Typography>
                        <Switch
                            checked={isAnnual}
                            onChange={(e) => setIsAnnual(e.target.checked)}
                            inputProps={{
                                id: 'plan-checkbox',
                                'aria-label': 'Plan checkbox',
                            }}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: '#048cec',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: '#048cec',
                                },
                            }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                                sx={{
                                    color: isAnnual ? '#1a202c' : 'text.disabled',
                                    fontFamily: 'Barlow, sans-serif',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    letterSpacing: '0.1em',
                                }}
                            >
                                {t('PricingCards04')}
                            </Typography>
                            {isAnnual && (
                                <Box
                                    sx={{
                                        backgroundColor: '#10b981',
                                        color: 'white',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        fontFamily: 'Barlow, sans-serif',
                                    }}
                                >
                                    {t('PricingCards05')} {PRICING_PLANS.individual.yearlySavings}%
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Pricing Cards */}
                <Grid container spacing={{ xs: 3, md: 4 }}>
                    {pricingPlans.map((plan, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 3,
                                    boxShadow: plan.popular
                                        ? '0 8px 32px rgba(4, 140, 236, 0.2)'
                                        : '0 4px 20px rgba(0, 0, 0, 0.08)',
                                    border: plan.popular
                                        ? '2px solid #048cec'
                                        : '1px solid rgba(0, 0, 0, 0.05)',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'visible',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: plan.popular
                                            ? '0 12px 40px rgba(4, 140, 236, 0.3)'
                                            : '0 8px 32px rgba(0, 0, 0, 0.12)',
                                    },
                                }}
                            >
                                {plan.popular && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: -12,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            backgroundColor: '#048cec',
                                            color: 'white',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: 2,
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            fontFamily: 'Barlow, sans-serif',
                                            zIndex: 10,
                                            boxShadow: '0 2px 8px rgba(4, 140, 236, 0.3)',
                                        }}
                                    >
                                        {t('PricingCards06')}
                                    </Box>
                                )}

                                <CardContent
                                    sx={{
                                        p: { xs: 2.5, md: 3 },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1,
                                    }}
                                >
                                    {/* Plan Title */}
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontFamily: 'Barlow, sans-serif',
                                            fontSize: { xs: '1.25rem', md: '1.5rem' },
                                            fontWeight: 700,
                                            color: '#1a202c',
                                            mb: 0.5,
                                        }}
                                    >
                                        {plan.title}
                                    </Typography>

                                    {/* Price */}
                                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, justifyContent: 'center' }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: 'Barlow, sans-serif',
                                                    fontSize: { xs: '2rem', md: '2.5rem' },
                                                    fontWeight: 800,
                                                    color: plan.color,
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {plan.price}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: '#9ca3af',
                                                    fontFamily: 'Barlow, sans-serif',
                                                    fontSize: { xs: '1rem', md: '1.2rem' },
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {t('PricingCards07')}
                                            </Typography>
                                        </Box>
                                        {!plan.isFree && (
                                            <Typography
                                                sx={{
                                                    color: '#9ca3af',
                                                    fontFamily: 'Barlow, sans-serif',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500,
                                                    mt: 0.5,
                                                }}
                                            >
                                                {isAnnual ? t('PricingCards08') : t('PricingCards09')}
                                            </Typography>
                                        )}
                                        {plan.hasSlider && (
                                            <Typography
                                                sx={{
                                                    color: '#6b7280',
                                                    fontFamily: 'Barlow, sans-serif',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 400,
                                                    mt: 0.5,
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {t('PricingCards10')} ({isAnnual ? PRICING_PLANS.adminClub.monthlyFromYearly : PRICING_PLANS.adminClub.monthly}€) + {sliderValue} {sliderValue > 1 ? t('PricingCards12') : t('PricingCards11')} ({isAnnual ? PRICING_PLANS.coachLinkedToAdmin.monthlyFromYearly : PRICING_PLANS.coachLinkedToAdmin.monthly}€ {t('PricingCards13')})
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Slider for Club plan */}
                                    {plan.hasSlider && (
                                        <Box sx={{ mb: 2 }}>
                                            <Slider
                                                value={sliderValue}
                                                min={1}
                                                max={21}
                                                step={1}
                                                onChange={handleSliderChange}
                                                sx={{
                                                    color: plan.color,
                                                    '& .MuiSlider-thumb': {
                                                        backgroundColor: plan.color,
                                                    },
                                                    '& .MuiSlider-track': {
                                                        backgroundColor: plan.color,
                                                    },
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    color: plan.color,
                                                    fontFamily: 'Barlow, sans-serif',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    mt: 0.5,
                                                }}
                                            >
                                                {sliderValue} {t('PricingCards02')}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Features */}
                                    <Box sx={{ flexGrow: 1, mb: 2 }}>
                                        {/* Add "Everything from Individual" for Club plan as first item */}
                                        {index === 2 && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    mb: 1,
                                                    p: 1,
                                                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                                    borderRadius: 1,
                                                    border: '1px solid rgba(16, 185, 129, 0.2)',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        color: '#10b981',
                                                        fontSize: '0.8rem',
                                                        fontWeight: 700,
                                                        mr: 1,
                                                        mt: 0.1,
                                                        flexShrink: 0,
                                                        fontFamily: 'Barlow, sans-serif',
                                                    }}
                                                >
                                                    +
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        color: '#10b981',
                                                        fontFamily: 'Barlow, sans-serif',
                                                        fontSize: '0.8rem',
                                                        lineHeight: 1.4,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {t('PricingCards14')}
                                                </Typography>
                                            </Box>
                                        )}

                                        {plan.features.map((feature, featureIndex) => (
                                            <Box
                                                key={featureIndex}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    mb: 1,
                                                }}
                                            >
                                                <CheckRounded
                                                    sx={{
                                                        color: plan.color,
                                                        fontSize: '1rem',
                                                        mr: 1,
                                                        mt: 0.1,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontFamily: 'Barlow, sans-serif',
                                                        fontSize: '0.8rem',
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {feature}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                    {/* CTA Button */}
                                    <Button
                                        variant="contained"
                                        href={appSignUp}
                                        sx={{
                                            background: plan.color,
                                            color: 'white',
                                            py: 1,
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            borderRadius: '6px',
                                            textTransform: 'none',
                                            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                background: plan.color,
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 5px 14px rgba(0, 0, 0, 0.15)',
                                            },
                                        }}
                                    >
                                        {t('Header01')}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
}